/**
 * Query Monitoring Utility
 * 
 * Tracks Firestore query performance and logs metrics for optimization.
 * Use this to identify slow queries and monitor query patterns.
 */

/**
 * Monitor a Firestore query and track its performance
 * @param {string} queryName - Descriptive name for the query
 * @param {Function} queryFn - Async function that executes the query
 * @returns {Promise} - Returns the query result
 */
export async function monitorQuery(queryName, queryFn) {
  const start = performance.now();
  
  try {
    const result = await queryFn();
    const duration = performance.now() - start;
    
    // Log query performance
    logQueryMetrics({
      queryName,
      duration,
      status: 'success',
      resultCount: result?.docs?.length || result?.size || 0
    });
    
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    
    // Log query error
    logQueryMetrics({
      queryName,
      duration,
      status: 'error',
      errorCode: error.code,
      errorMessage: error.message
    });
    
    throw error;
  }
}

/**
 * Log query metrics to console and analytics
 * @param {Object} metrics - Query metrics object
 */
function logQueryMetrics(metrics) {
  const { queryName, duration, status, resultCount, errorCode, errorMessage } = metrics;
  
  // Console logging (development)
  if (process.env.NODE_ENV === 'development') {
    if (status === 'success') {
      console.log(
        `[Query Performance] ${queryName}: ${duration.toFixed(2)}ms (${resultCount} docs)`
      );
      
      // Warn on slow queries
      if (duration > 2000) {
        console.warn(`⚠️ Slow query detected: ${queryName} took ${duration.toFixed(2)}ms`);
      }
    } else {
      console.error(
        `[Query Error] ${queryName}: ${duration.toFixed(2)}ms - ${errorCode}: ${errorMessage}`
      );
    }
  }
  
  // Send to analytics (if available)
  if (window.gtag && status === 'success') {
    window.gtag('event', 'query_performance', {
      query_name: queryName,
      duration_ms: Math.round(duration),
      result_count: resultCount,
      event_category: 'firestore',
      event_label: queryName
    });
  } else if (window.gtag && status === 'error') {
    window.gtag('event', 'query_error', {
      query_name: queryName,
      duration_ms: Math.round(duration),
      error_code: errorCode,
      event_category: 'firestore',
      event_label: queryName
    });
  }
}

/**
 * Track multiple queries in parallel
 * @param {Array} queries - Array of {name, fn} objects
 * @returns {Promise<Array>} - Array of query results
 */
export async function monitorParallelQueries(queries) {
  const start = performance.now();
  
  try {
    const results = await Promise.all(
      queries.map(({ name, fn }) => monitorQuery(name, fn))
    );
    
    const totalDuration = performance.now() - start;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `[Parallel Queries] Completed ${queries.length} queries in ${totalDuration.toFixed(2)}ms`
      );
    }
    
    return results;
  } catch (error) {
    const totalDuration = performance.now() - start;
    console.error(
      `[Parallel Queries] Failed after ${totalDuration.toFixed(2)}ms:`,
      error
    );
    throw error;
  }
}

/**
 * Create a monitored version of a query function
 * @param {string} queryName - Name for the query
 * @param {Function} queryFn - Query function to wrap
 * @returns {Function} - Wrapped query function
 */
export function createMonitoredQuery(queryName, queryFn) {
  return async (...args) => {
    return monitorQuery(queryName, () => queryFn(...args));
  };
}
